import { useRef, type ReactNode } from 'react';
import { useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';

/**
 * browse-kit - the shared plumbing of the browse-all Lookup tables (2026-07-14 rework, Portal
 * Operations Lookup parity): a sortable header cell, the search-toolbar row, and the virtualized
 * spacer-row table body the Portal browse tables use (@tanstack/react-virtual + top/bottom spacer
 * <tr>s + measureElement dynamic heights, so an expanded detail row of any height virtualizes
 * correctly inside a plain <table>).
 *
 * Scroll model: each browse table scrolls inside its own max-height container (the Portal shape);
 * the .dt sticky thead pins to that container's top. Base row estimate matches --row-h-table
 * (34px); measureElement re-measures per row so density variants and tall detail rows stay exact.
 */

export type SortDir = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  dir: SortDir;
}

/** Clicking the active column flips the direction; a new column starts ascending (Portal rule). */
export function toggleSort<K extends string>(s: SortState<K>, key: K): SortState<K> {
  return s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' };
}

/** Decorate-sort-undecorate with a stable tiebreak (Portal browse-table sort). */
export function sortRows<T, K extends string>(
  rows: T[],
  sort: SortState<K>,
  value: (r: T, key: K) => string | number,
  tiebreak: (r: T) => string,
): T[] {
  const dir = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const va = value(a.r, sort.key);
      const vb = value(b.r, sort.key);
      let c = 0;
      if (typeof va === 'number' && typeof vb === 'number') c = va - vb;
      else c = String(va).localeCompare(String(vb));
      if (c !== 0) return c * dir;
      const t = tiebreak(a.r).localeCompare(tiebreak(b.r));
      return t !== 0 ? t : a.i - b.i;
    })
    .map((x) => x.r);
}

/** A sortable header cell (the house glyph vocabulary: neutral U+21C5, asc U+25B4, desc U+25BE). */
export function SortHead<K extends string>({
  col,
  label,
  sort,
  onSort,
  numeric,
}: {
  col: K;
  label: string;
  sort: SortState<K>;
  onSort: (key: K) => void;
  numeric?: boolean;
}) {
  const active = sort.key === col;
  const glyph = active ? (sort.dir === 'asc' ? '▴' : '▾') : '⇅';
  return (
    <th
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      style={numeric ? { textAlign: 'right' } : undefined}
    >
      <button
        type="button"
        onClick={() => onSort(col)}
        title={`Sort by ${label}`}
        style={{
          all: 'inherit',
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 4,
          cursor: 'pointer',
          padding: 0,
          border: 'none',
          background: 'transparent',
        }}
      >
        {label}
        <span aria-hidden="true" style={{ opacity: active ? 1 : 0.45 }}>{glyph}</span>
      </button>
    </th>
  );
}

/** The browse toolbar: search box + shown-of-total count + a right-side slot (export button...). */
export function BrowseToolbar({
  search,
  onSearch,
  placeholder,
  ariaLabel,
  shown,
  total,
  noun,
  right,
}: {
  search: string;
  onSearch: (v: string) => void;
  placeholder: string;
  ariaLabel: string;
  shown: number;
  total: number;
  noun: string;
  right?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
      <input
        className="text-input"
        type="search"
        aria-label={ariaLabel}
        style={{ maxWidth: 380 }}
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={placeholder}
      />
      <span className="muted small" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {shown.toLocaleString()}
        {shown !== total ? ` of ${total.toLocaleString()}` : ''} {noun}
      </span>
      {right && <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>{right}</span>}
    </div>
  );
}

/** One virtualized display item: a data row, or the detail row interleaved under the open row. */
export type BrowseItem<T> = { kind: 'row'; row: T } | { kind: 'detail'; row: T };

/** Interleave the open row's detail item right after it (the Portal displayItems shape). */
export function interleaveDetail<T>(rows: T[], isOpen: (r: T) => boolean): BrowseItem<T>[] {
  const out: BrowseItem<T>[] = [];
  for (const r of rows) {
    out.push({ kind: 'row', row: r });
    if (isOpen(r)) out.push({ kind: 'detail', row: r });
  }
  return out;
}

export interface BrowseVirtual {
  scrollRef: React.RefObject<HTMLDivElement>;
  items: VirtualItem[];
  padTop: number;
  padBottom: number;
  measureRef: (el: HTMLTableRowElement | null) => void;
  /** Bring one display item into the viewport under the sticky header (a drilled-in or linked row). */
  scrollToIndex: (index: number) => void;
}

/** The Portal browse virtualizer: 34px base rows, taller detail estimate, overscan 12, spacer-row
 *  padding (top/bottom <tr>s) + measureElement so real heights win over estimates. */
export function useBrowseVirtual<T>(
  displayItems: BrowseItem<T>[],
  keyOf: (item: BrowseItem<T>) => string,
  detailEstimate: number,
): BrowseVirtual {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer: Virtualizer<HTMLDivElement, HTMLTableRowElement> = useVirtualizer({
    count: displayItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (displayItems[i]?.kind === 'detail' ? detailEstimate : 34),
    overscan: 12,
    scrollPaddingStart: 34,
    getItemKey: (i) => {
      const it = displayItems[i];
      return it ? keyOf(it) : i;
    },
  });
  const items = virtualizer.getVirtualItems();
  const padTop = items.length ? items[0]!.start : 0;
  const padBottom = items.length
    ? virtualizer.getTotalSize() - items[items.length - 1]!.end
    : 0;
  return {
    scrollRef, items, padTop, padBottom, measureRef: virtualizer.measureElement,
    scrollToIndex: (index: number) => virtualizer.scrollToIndex(index, { align: 'start' }),
  };
}

/** The spacer rows framing the virtual window (colSpan = the table's full column count). */
export function SpacerRow({ height, colSpan }: { height: number; colSpan: number }) {
  if (height <= 0) return null;
  return (
    <tr aria-hidden="true">
      <td colSpan={colSpan} style={{ height, padding: 0, border: 'none' }} />
    </tr>
  );
}
